import { Button, Grid, Typography } from "@mui/material";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import photo from "../photo.jpg";
import Resume from "../UtsavPatel.pdf";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { AppContext } from "../App";
import { motion, AnimatePresence } from "framer-motion";

gsap.registerPlugin(ScrollTrigger);

// ─────────────────────────────────────────────────────────────────────────────
// Scramble text — characters cycle through random glyphs before resolving
// ─────────────────────────────────────────────────────────────────────────────
const CHARS = '!<>—#@$%^&*+=?ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

const ScrambleText = ({ text, active, refProp }) => {
  const [output, setOutput] = useState(() =>
    text.split('').map(c => c === ' ' ? ' ' : CHARS[Math.floor(Math.random() * CHARS.length)]).join('')
  );

  useEffect(() => {
    if (!active) return;
    let frame = 0;
    let raf;
    const resolved = new Array(text.length).fill(false);
    const SPEED = 2.0; // frames per character resolve

    const step = () => {
      let out = '';
      let done = true;
      for (let i = 0; i < text.length; i++) {
        if (text[i] === ' ') { out += ' '; continue; }
        if (resolved[i]) { out += text[i]; continue; }
        done = false;
        if (frame > i * SPEED) {
          resolved[i] = true;
          out += text[i];
        } else {
          out += CHARS[Math.floor(Math.random() * CHARS.length)];
        }
      }
      setOutput(out);
      frame++;
      if (!done) raf = requestAnimationFrame(step);
    };

    // Short delay so loader has fully exited before scramble starts
    const timer = setTimeout(() => { raf = requestAnimationFrame(step); }, 120);
    return () => { clearTimeout(timer); cancelAnimationFrame(raf); };
  }, [active, text]);

  return (
    <Typography
      ref={refProp}
      fontSize={28}
      fontWeight={600}
      fontFamily="Alkatra"
      style={{ letterSpacing: '0.01em', minHeight: '1.4em' }}
    >
      {output}
    </Typography>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Clip-path role cycler — text wipes in/out like a film reveal
// ─────────────────────────────────────────────────────────────────────────────
const ROLES = ['Software Engineer', 'Frontend Developer', 'React Architect', 'UI Craftsman'];

const ClipRoleCycler = ({ active }) => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => setIndex(i => (i + 1) % ROLES.length), 3000);
    return () => clearInterval(id);
  }, [active]);

  return (
    <div style={{ overflow: 'hidden', position: 'relative' }}>
      <AnimatePresence mode="wait">
        <motion.span
          key={index}
          initial={{ clipPath: 'inset(0 100% 0 0)', opacity: 1 }}
          animate={{ clipPath: 'inset(0 0% 0 0)', opacity: 1 }}
          exit={{ clipPath: 'inset(0 0 0 100%)', opacity: 0 }}
          transition={{ duration: 0.52, ease: [0.22, 1, 0.36, 1] }}
          style={{
            display: 'block',
            fontFamily: 'Poppins',
            fontSize: 30,
            fontWeight: 600,
            color: 'inherit',
            whiteSpace: 'nowrap',
          }}
        >
          {ROLES[index]}
        </motion.span>
      </AnimatePresence>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Magnetic button — nudges toward cursor, springs back on leave
// ─────────────────────────────────────────────────────────────────────────────
const MagneticButton = ({ children }) => {
  const ref = useRef(null);
  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    gsap.to(ref.current, {
      x: (e.clientX - (r.left + r.width / 2)) * 0.3,
      y: (e.clientY - (r.top + r.height / 2)) * 0.3,
      duration: 0.35,
      ease: 'power2.out',
    });
  };
  const onLeave = () =>
    gsap.to(ref.current, { x: 0, y: 0, duration: 0.65, ease: 'elastic.out(1, 0.5)' });
  return (
    <div ref={ref} className="magnetic-wrap" onMouseMove={onMove} onMouseLeave={onLeave}>
      {children}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
const Dashboard = () => {
  const { loaded } = useContext(AppContext);

  const sectionRef = useRef(null);
  const greetingRef = useRef(null);
  const typeWrapRef = useRef(null);
  const ctaRef = useRef(null);

  // Nested photo wrappers — each owns one transform axis so they never fight:
  //   photoColRef  → y (ScrollTrigger scrub)
  //   floatWrapRef → y (continuous bob)
  //   photoWrapRef → rotateX/Y (3-D tilt on hover)
  //   photoRef     → scale + autoAlpha (entrance)
  const photoColRef = useRef(null);
  const floatWrapRef = useRef(null);
  const photoWrapRef = useRef(null);
  const photoRef = useRef(null);

  const scrollIndicatorRef = useRef(null);
  const scrollLineRef = useRef(null);

  // Ambient glow orb that follows the mouse slowly
  const glowRef = useRef(null);

  // ── Mouse: glow orb + layer parallax ──────────────────────────────────────
  const handleMouseMove = useCallback((e) => {
    if (!sectionRef.current) return;
    const rect = sectionRef.current.getBoundingClientRect();
    const xPct = ((e.clientX - rect.left) / rect.width) * 100;
    const yPct = ((e.clientY - rect.top) / rect.height) * 100;

    // Glow orb drifts very slowly toward cursor
    if (glowRef.current) {
      gsap.to(glowRef.current, {
        left: `${xPct}%`,
        top: `${yPct}%`,
        duration: 1.4,
        ease: 'power2.out',
      });
    }

    // Text layers parallax — each layer at a different depth
    const cx = (e.clientX - rect.left) / rect.width - 0.5; // -0.5 → 0.5
    const cy = (e.clientY - rect.top) / rect.height - 0.5;
    if (greetingRef.current) gsap.to(greetingRef.current, { x: cx * -24, y: cy * -14, duration: 0.75, ease: 'power2.out' });
    if (typeWrapRef.current) gsap.to(typeWrapRef.current, { x: cx * -14, y: cy * -8, duration: 0.75, ease: 'power2.out' });
    if (ctaRef.current) gsap.to(ctaRef.current, { x: cx * -7, y: cy * -4, duration: 0.75, ease: 'power2.out' });
  }, []);

  const handleMouseLeave = useCallback(() => {
    gsap.to([greetingRef.current, typeWrapRef.current, ctaRef.current], {
      x: 0, y: 0, duration: 1, ease: 'power2.out',
    });
  }, []);

  // ── Main entrance — fires once after the page loader exits ────────────────
  useEffect(() => {
    if (!loaded) return;

    const ctx = gsap.context(() => {
      // GSAP owns these initial states (not JSX inline styles)
      gsap.set(typeWrapRef.current, { autoAlpha: 0, y: 30 });
      gsap.set(ctaRef.current, { autoAlpha: 0, y: 22 });
      gsap.set(photoRef.current, { autoAlpha: 0, y: 70, scale: 0.84 });
      gsap.set(scrollIndicatorRef.current, { autoAlpha: 0, y: 12 });

      const tl = gsap.timeline({ delay: 0.08 });

      tl
        // Role text slides up
        .to(typeWrapRef.current, {
          autoAlpha: 1, y: 0, duration: 0.72, ease: 'power3.out',
        })
        // CTA buttons
        .to(ctaRef.current, {
          autoAlpha: 1, y: 0, duration: 0.6, ease: 'power3.out',
        }, '-=0.44')
        // Photo scales up
        .to(photoRef.current, {
          autoAlpha: 1, y: 0, scale: 1, duration: 1.15, ease: 'power4.out',
        }, '-=0.65')
        // Scroll indicator last
        .to(scrollIndicatorRef.current, {
          autoAlpha: 1, y: 0, duration: 0.55, ease: 'power2.out',
        }, '-=0.28');

      // ── Continuous float on the float wrapper (y-axis, independent of scroll)
      gsap.to(floatWrapRef.current, {
        y: 16,
        duration: 2.9,
        ease: 'sine.inOut',
        repeat: -1,
        yoyo: true,
        delay: 1.7,
      });

      // ── Scroll indicator pulse
      gsap.fromTo(
        scrollLineRef.current,
        { scaleY: 0, transformOrigin: 'top center' },
        {
          scaleY: 1,
          duration: 0.88,
          ease: 'power2.inOut',
          repeat: -1,
          yoyo: true,
          repeatDelay: 0.35,
          delay: 2.3,
        }
      );

      // ── Photo column parallax — drifts slower than scroll
      gsap.to(photoColRef.current, {
        y: 90,
        ease: 'none',
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top top',
          end: 'bottom top',
          scrub: 1.5,
        },
      });
    }, sectionRef);

    return () => ctx.revert();
  }, [loaded]);

  // ── 3-D tilt on photo hover (rotateX/Y independent of float & scroll)
  const handleTilt = (e) => {
    if (!photoWrapRef.current) return;
    const r = photoWrapRef.current.getBoundingClientRect();
    gsap.to(photoWrapRef.current, {
      rotateY: ((e.clientX - r.left) / r.width - 0.5) * 20,
      rotateX: -((e.clientY - r.top) / r.height - 0.5) * 13,
      duration: 0.4,
      ease: 'power2.out',
      transformPerspective: 900,
    });
  };
  const handleTiltReset = () =>
    gsap.to(photoWrapRef.current, {
      rotateY: 0, rotateX: 0,
      duration: 0.85,
      ease: 'elastic.out(1, 0.45)',
    });

  return (
    <Grid
      ref={sectionRef}
      component="div"
      id="dashboard"
      minHeight="100vh"
      justifyContent="center"
      alignItems="center"
      display="flex"
      py={2}
      container
      style={{ position: 'relative' }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {/* ── Ambient glow orb — slow-chasing atmospheric light ─────────────── */}
      <div
        ref={glowRef}
        style={{
          position: 'absolute',
          left: '35%',
          top: '50%',
          width: '50vw',
          height: '50vw',
          transform: 'translate(-50%, -50%)',
          background: 'radial-gradient(circle, rgba(21,152,149,0.09) 0%, transparent 62%)',
          borderRadius: '50%',
          filter: 'blur(70px)',
          pointerEvents: 'none',
          willChange: 'left, top',
          zIndex: 0,
        }}
      />

      {/* ── LEFT ─────────────────────────────────────────────────────────── */}
      <Grid
        item
        xs={12}
        md={6}
        justifyContent="center"
        alignItems="center"
        display="flex"
        flexDirection="column"
        style={{ position: 'relative', zIndex: 1 }}
      >
        <div style={{ width: '80%' }}>
          {/* Scramble greeting — decrypts to final text after loader exits */}
          <ScrambleText
            text="Hello Fella..!"
            active={loaded}
            refProp={greetingRef}
          />

          {/* Role section — subtitle + clip-reveal cycling role */}
          <div ref={typeWrapRef} style={{ marginTop: 8 }}>
            <Typography
              fontFamily="Poppins"
              fontSize={12}
              sx={{
                opacity: 0.42,
                letterSpacing: '0.07em',
                textTransform: 'uppercase',
                mb: 0.5,
              }}
            >
              I craft things for the web as a
            </Typography>
            <ClipRoleCycler active={loaded} />
          </div>
        </div>

        <br />
        <br />

        {/* CTA buttons */}
        <Grid
          ref={ctaRef}
          item
          justifyContent="center"
          alignItems="center"
          display="flex"
          gap="20px"
        >
          <MagneticButton>
            <Button href={Resume} download variant="outlined" color="primary">
              Download Resume
            </Button>
          </MagneticButton>
          <MagneticButton>
            <Button href="https://uv-todo.netlify.app/" variant="text" color="primary" target="_blank">
              Explore Projects
            </Button>
          </MagneticButton>
        </Grid>
      </Grid>

      {/* ── RIGHT: nested wrappers for non-conflicting transforms ──────────── */}
      {/*
          photoColRef  → y scrub (scroll parallax)
          floatWrapRef → y loop (continuous float)
          photoWrapRef → rotateX/Y (3-D tilt)
          photoRef     → scale + autoAlpha (entrance)
      */}
      <Grid
        ref={photoColRef}
        item
        xs={12}
        md={6}
        justifyContent="center"
        alignItems="center"
        display="flex"
        style={{ willChange: 'transform', position: 'relative', zIndex: 1 }}
      >
        <div ref={floatWrapRef} style={{ width: '80%', willChange: 'transform' }}>
          <div
            ref={photoWrapRef}
            onMouseMove={handleTilt}
            onMouseLeave={handleTiltReset}
            style={{ transformStyle: 'preserve-3d', willChange: 'transform' }}
          >
            <img
              ref={photoRef}
              className="image-photo"
              src={photo}
              alt="Utsav Patel"
              height="auto"
              width="100%"
              style={{ display: 'block' }}
            />
          </div>
        </div>
      </Grid>

      {/* ── Scroll indicator ─────────────────────────────────────────────── */}
      <div
        ref={scrollIndicatorRef}
        style={{
          position: 'absolute',
          bottom: 28,
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 8,
          pointerEvents: 'none',
          zIndex: 1,
        }}
      >
        <Typography
          fontFamily="Poppins"
          fontSize={10}
          fontWeight={500}
          sx={{ letterSpacing: '0.3em', textTransform: 'uppercase', opacity: 0.5 }}
        >
          Scroll
        </Typography>
        <div
          ref={scrollLineRef}
          style={{
            width: 1,
            height: 50,
            background: '#159895',
            transformOrigin: 'top center',
          }}
        />
      </div>
    </Grid>
  );
};

export default Dashboard;
